import type { ClassifierConfig } from '../api/admin';

// ---------------------------------------------------------------------------
// Generic classifier-value → display-label resolution.
//
// Historically this module shipped three hardcoded Romanian dictionaries
// (`RO_CATEGORY`, `RO_IMAGING_MODALITY`, `RO_BODY_REGION`) — one per
// medical classifier we knew about. That hardcoded everything to one
// domain (medical) and one locale (Romanian) at build time.
//
// The classifier registry is now admin-managed: every active classifier
// ships its own taxonomy entries (`code`, `label_ro`, `label_en`) via
// `GET /api/classifiers/config`. The UI looks values up against that
// registry instead of any baked-in dict, so swapping the medical seed
// for any other vertical "just works" — no code change required.
// ---------------------------------------------------------------------------

/** Pretty-print a raw classifier code by replacing underscores with spaces.
 *  Used as the universal fallback when the registry has no entry for the
 *  given (classifier, code) pair (admin hasn't curated it yet, or the
 *  LLM emitted a freeform value that's still in the pending-inbox). */
export function humanizeCode(code: string | null | undefined): string {
  if (!code) return '';
  return String(code).replace(/_/g, ' ');
}

/**
 * Look up the display label for a single (classifierKey, code) pair against
 * the runtime classifier registry. Resolution order:
 *
 *   1. Exact match on `entries[*].code` → use that entry's `label_ro`
 *      (or `label_en` when `locale === 'en'` and the RO label is empty).
 *   2. Case-insensitive match on any alias in the same entry.
 *   3. Case-insensitive match on the entry label itself (so a value
 *      the LLM already emitted in its display form round-trips cleanly).
 *   4. Last-resort fallback: `humanizeCode(code)` (turns `lab_report`
 *      into `lab report`).
 *
 * Callers pass the `classifiers` array from {@link useClassifiersConfig}.
 * Returning `''` for an empty/nullish input matches the old per-classifier
 * formatters so the call-sites don't have to special-case it.
 */
export function formatClassifierValue(
  classifiers: ClassifierConfig[] | null | undefined,
  classifierKey: string,
  code: string | null | undefined,
  locale: 'ro' | 'en' = 'ro'
): string {
  if (!code) return '';
  const raw = String(code).trim();
  if (!raw) return '';

  const classifier = classifiers?.find((c) => c.key === classifierKey);
  const entries = classifier?.entries;
  if (entries && entries.length > 0) {
    const lowered = raw.toLowerCase();
    let match = entries.find((e) => e.code === raw);
    if (!match) {
      match = entries.find((e) => e.code.toLowerCase() === lowered);
    }
    if (!match) {
      match = entries.find((e) => (e.aliases ?? []).some((a) => a.toLowerCase() === lowered));
    }
    if (!match) {
      match = entries.find(
        (e) =>
          e.label_ro.toLowerCase() === lowered ||
          (e.label_en ?? '').toLowerCase() === lowered
      );
    }
    if (match) {
      const en = match.label_en?.trim();
      const ro = match.label_ro?.trim();
      if (locale === 'en' && en) return en;
      if (ro) return ro;
      if (en) return en;
      return humanizeCode(match.code);
    }
  }
  return humanizeCode(raw);
}

/** Free-form normalization of a search tag (used by the member card preview).
 *  Tags don't live in the classifier registry — they're free-text per
 *  document — so we just collapse underscores. */
export function formatClassificationTag(tag: string): string {
  return humanizeCode(tag);
}

// ---------------------------------------------------------------------------
// File-type display (storage-level concern, independent of classifiers).
// ---------------------------------------------------------------------------

// Known extensions we surface in the UI for the per-document file_type display.
// Anything outside this set falls back to whatever the server stored (so we don't
// guess wrong for esoteric extensions).
const _KNOWN_DOC_EXTS = new Set([
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'docx',
  'doc',
  'txt',
  'rtf',
]);

/**
 * Resolves the displayed file type. When the server returns "bin" — which
 * happens for legacy E2EE uploads that hardcoded `file_type="bin"` regardless
 * of input — we fall back to the extension of the original filename so the doc
 * card / details panel still show "PDF" / "DOCX" / "JPG" instead of "BIN".
 */
export function displayFileType(rawType: string | null | undefined, originalFilename?: string | null): string {
  const raw = (rawType || '').trim().toLowerCase();
  if (raw && raw !== 'bin') return raw.toUpperCase();
  if (originalFilename) {
    const dot = originalFilename.lastIndexOf('.');
    if (dot >= 0 && dot < originalFilename.length - 1) {
      const ext = originalFilename.slice(dot + 1).toLowerCase();
      if (_KNOWN_DOC_EXTS.has(ext)) return ext.toUpperCase();
    }
  }
  return raw ? raw.toUpperCase() : 'FIȘIER';
}

// ---------------------------------------------------------------------------
// OCR pipeline status (technical, domain-agnostic).
// ---------------------------------------------------------------------------

export function formatOcrStatusShort(status: string | null | undefined): string | null {
  if (!status) return null;
  const m: Record<string, string> = {
    queued: 'În coadă…',
    completed: 'Procesat',
    processing: 'Procesare OCR…',
    processing_started: 'Pornire OCR…',
    ocr_extraction: 'Extragere text…',
    loading_images: 'Încărcare…',
    failed: 'Eroare OCR',
    summary_generating: 'Rezumat…',
    summary_completed: 'Procesat',
    summary_error: 'Eroare rezumat',
    classification_generating: 'Clasificare AI…',
    classification_completed: 'Clasificat',
    classification_error: 'Eroare clasificare',
  };
  return m[status] ?? status;
}
